import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { SearchInput } from "@/components/common/SearchInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PRIORITY_DOT, TYPE_COLOR } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Globe } from "lucide-react";

export default function GlobalBoard() {
  const navigate = useNavigate();
  const [projectFilter, setProjectFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: () => get("/pm/projects") });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const umap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const pmap = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p.name])), [projects]);

  const { data: allItems = [], isLoading } = useQuery({
    queryKey: ["all-items", projects.map((p) => p.id).join(",")],
    queryFn: async () => {
      const lists = await Promise.all(projects.map((p) => get(`/pm/projects/${p.id}/items`)));
      return lists.flat();
    },
    enabled: projects.length > 0,
  });

  const types = useMemo(() => [...new Set(allItems.map((i) => i.type))].filter(Boolean).sort(), [allItems]);
  const priorities = useMemo(() => [...new Set(allItems.map((i) => i.priority))].filter(Boolean).sort(), [allItems]);

  const filtered = useMemo(() => {
    return allItems.filter((i) => {
      if (projectFilter !== "all" && i.project_id !== projectFilter) return false;
      if (search && !i.title?.toLowerCase().includes(search.toLowerCase())) return false;
      if (typeFilter !== "all" && i.type !== typeFilter) return false;
      if (priorityFilter !== "all" && i.priority !== priorityFilter) return false;
      if (assigneeFilter !== "all" && i.assignee_id !== assigneeFilter) return false;
      return true;
    });
  }, [allItems, projectFilter, search, typeFilter, priorityFilter, assigneeFilter]);
  const open = filtered.filter((i) => !i.column_id?.endsWith("col-done"));
  const allOpen = allItems.filter((i) => !i.column_id?.endsWith("col-done"));

  if (isLoading) return <Skeleton className="h-64" />;

  return (
    <div>
      <PageHeader title="Global Board" subtitle="All open tickets across every project.">
        <Select value={projectFilter} onValueChange={setProjectFilter}>
          <SelectTrigger className="w-56" data-testid="global-board-project-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All projects</SelectItem>
            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </PageHeader>

      <div className="mb-4 flex items-center gap-3">
        <SearchInput testid="globalboard-search" placeholder="Search title" value={search} onChange={setSearch} />
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-36" data-testid="globalboard-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-36" data-testid="globalboard-priority-filter"><SelectValue placeholder="All priorities" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {priorities.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
          <SelectTrigger className="w-44" data-testid="globalboard-assignee-filter"><SelectValue placeholder="All assignees" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All assignees</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {allOpen.length === 0 ? <EmptyState icon={Globe} title="Nothing open" /> :
        open.length === 0 ? <EmptyState icon={Globe} title="No items match your filters" /> : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm" data-testid="global-board-table">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold">Title</th>
                <th className="px-4 py-3 font-semibold">Project</th>
                <th className="px-4 py-3 font-semibold">Priority</th>
                <th className="px-4 py-3 font-semibold">Assignee</th>
                <th className="px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {open.map((it) => (
                <tr key={it.id} data-testid={`global-item-${it.id}`} onClick={() => navigate(`/pm/projects/${it.project_id}/items/${it.id}`)}
                  className="cursor-pointer border-b border-border last:border-0 hover:bg-muted/50">
                  <td className="px-4 py-3"><StatusBadge family={TYPE_COLOR[it.type]} label={it.type} /></td>
                  <td className="px-4 py-3 font-medium">{it.title} {it.blocked && <StatusBadge family="red" label="Blocked" />}</td>
                  <td className="px-4 py-3 text-muted-foreground">{pmap[it.project_id]}</td>
                  <td className="px-4 py-3"><span className="flex items-center gap-1.5 capitalize"><span className={cn("h-2 w-2 rounded-full", PRIORITY_DOT[it.priority])} />{it.priority}</span></td>
                  <td className="px-4 py-3"><div className="flex items-center gap-1.5"><Avatar user={umap[it.assignee_id]} size="xs" /><span className="text-xs">{umap[it.assignee_id]?.name}</span></div></td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{it.column_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
